import { Pivot, PivotItem } from "@fluentui/react";
import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { TabsContainer } from "../../SharedComponents/TabsContainer";
import { logEvent, logException } from "../../helper/AppInsightsHelper";
import { useGetUIEnablementsSettingsQuery } from "../../slices/Home.slice";
import { CustomerViewTabs } from "../../Utils/Enums/Customer.constants";
import { buildDisabledFeatureLookup } from "../../Utils/js-common";
import CommunityDetailsContainer from "./CommunityDetailsContainer";
import ConsumptionRevenueTab from "./ConsumptionRevenueTab/ConsumptionRevenueTab";
import CRMCustomerProductContainer from "./CRMCustomerProductContainer";
import CustomerEngagementContainer from "./CustomerEngagementContainer";
import FeatureRequestContainer from "./FeatureRequestContainer";
import CustomerFeatureView from "./Features/CustomerFeatureView";
import PartnerContainer from "./PartnerContainer";
import ProductTelemetryView from "./ProductTelemetry/ProductTelemetryView";
import ProgramsContainer from "./ProgramsContainer";
import RFACustomerProductContainer from "./RFACustomerProductContainer";
import SubscriptionContainer from "./SubscriptionContainer";
import SupportContainer from "./SupportContainer";
import { CustomerHealthMTP } from "./CustomerHealthView/CustomerHealthMTP";
import { CustomerHealthPurview } from "./CustomerHealthView/CustomerHealthPurview";
import { CustomerHealthIntune } from "./CustomerHealthView/CustomerHealthIntune";
import type { HealthViewMode } from "./CustomerHealthView/useCustomerHealthHooks";
import { BaselineSecurityHealthTenantDetail } from "../HealthView/Containers/BaselineSecurityHealth/BaselineSecurityHealthTenantDetail";
import { useGetBaselineSecurityHealthCustomerDetailQuery } from "../../slices/BaselineSecurityHealth.slice";
import { Spinner, SpinnerSize, MessageBar, MessageBarType } from "@fluentui/react";

/**
 * Customer-tab content for the Baseline Security Health pivot. Pulls the
 * per-tenant detail from `Fn_S360_GetBaselineSecurityHealthCustomer(tenantId)`
 * via RTK Query and renders one of: loading, error, no-data, or the detail view.
 */
const BaselineSecurityHealthCustomerTab: React.FC<{ tenantId: string; tenantName?: string }> = ({ tenantId, tenantName }) => {
  const { data, isLoading, isError, error } = useGetBaselineSecurityHealthCustomerDetailQuery(tenantId, {
    skip: !tenantId,
  });

  const customProps = {
    tenantId: tenantId,
    tenantName: tenantName,
  };

  useEffect(() => {
    if (tenantId) {
      logEvent(
        "Baseline Security Health Tab - Customer Health",
        "Customer Page",
        "BaselineSecurityHealthCustomerTab",
        customProps
      );
    }
  }, [tenantId, tenantName]);

  useEffect(() => {
    if (isError) {
      logException(
        "Customer Page - Baseline Security Health Tab",
        "BaselineSecurityHealthCustomerTab",
        "getBaselineSecurityHealthCustomerDetail",
        error,
        customProps
      );
    }
  }, [isError, error]);

  useEffect(() => {
    // Tenant resolved successfully but has no Baseline Security Health data
    // (not in the pilot cohort) — track how often users land here to gauge
    // demand for expanding coverage.
    if (tenantId && !isLoading && !isError && data == null) {
      logEvent(
        "Baseline Security Health Tab - No Data For Tenant",
        "Customer Page",
        "BaselineSecurityHealthCustomerTab",
        customProps
      );
    }
  }, [tenantId, isLoading, isError, data]);

  if (!tenantId) {
    return (
      <MessageBar messageBarType={MessageBarType.info} isMultiline={false}>
        Open this page with a tenant id to view Ready for AI.
      </MessageBar>
    );
  }
  if (isLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
        <Spinner size={SpinnerSize.medium} label="Loading Ready for AI..." labelPosition="right" />
      </div>
    );
  }
  if (isError) {
    return (
      <MessageBar messageBarType={MessageBarType.error} isMultiline={false}>
        Unable to load Ready for AI data. Please retry or contact support.
      </MessageBar>
    );
  }
  if (!data) {
    return (
      <div
        style={{
          padding: "40px 24px",
          textAlign: "center",
          border: "1px dashed #C8C6C4",
          borderRadius: 8,
          backgroundColor: "#FAF9F8",
        }}
      >
        <span style={{ fontSize: 32, display: "block", marginBottom: 12 }}>🔒</span>
        <div style={{ fontWeight: 700, fontSize: 16, color: "#323130", marginBottom: 6 }}>
          Ready for AI data not available
        </div>
        <div style={{ fontSize: 13, color: "#605E5C", maxWidth: 400, margin: "0 auto" }}>
          This tenant is not currently in the Ready for AI pilot cohort.
        </div>
      </div>
    );
  }
  return <BaselineSecurityHealthTenantDetail tenant={data} showBackLink={false} />;
};

export const CustomerPivot = (props: any) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tpidFromQueryParam = searchParams.get("tpid") || "";
  const defaultTab = CustomerViewTabs.Product2; // Default to Product Telemetry (productv2)
  const defaultEngagementSection = "Customer Product"; // Default section for Customer Engagements
  const defaultHealthSection = "MTP"; // Default section for Customer Health
  const defaultHealthViewMode = "View by Tenant"; // Default view mode for Customer Health
  const initialTabParam = searchParams.get("tab");
  const [selectedTab, setSelectedTab] = React.useState<string>(
    initialTabParam === CustomerViewTabs.FeatureRequestViewByTenant
      || initialTabParam === CustomerViewTabs.FeatureRequestViewByTPID
      ? CustomerViewTabs.FeatureRequest
      : initialTabParam ?? defaultTab
  );
  // Feature Request sub-tab (View by Tenant / View by TPID). Both currently show the same data.
  const [featureRequestViewModeKey, setFeatureRequestViewModeKey] = React.useState<string>(
    initialTabParam === CustomerViewTabs.FeatureRequestViewByTPID
      ? CustomerViewTabs.FeatureRequestViewByTPID
      : CustomerViewTabs.FeatureRequestViewByTenant
  );
  const featureRequestTpid = props?.topParentId?.toString() || tpidFromQueryParam;
  const handleFeatureRequestViewModeChange = (item?: PivotItem) => {
    if (item?.props.itemKey) {
      setFeatureRequestViewModeKey(item.props.itemKey);
    }
  };
  const TABS_DATA = {
    pivot: { ariaLabel: "customer-pivot-component-aria-label" },
    pivotItems: [
      {
        headerText: "Product Telemetry",
        itemKey: CustomerViewTabs.Product2,
        component: () => <ProductTelemetryView tenantName={props.tenantName} />
      },
      {
        headerText: "Features Telemetry",
        itemKey: CustomerViewTabs.CustomerFeatureData,
        component: () => <CustomerFeatureView tenantName={props.tenantName} />
      },
      {
        headerText: "Consumption Revenue",
        itemKey: CustomerViewTabs.ConsumptionRevenue,
        component: () => <ConsumptionRevenueTab tenantTopParentId={props?.topParentId ?? 0} />
      },
      {
        headerText: "License and Subscription",
        itemKey: CustomerViewTabs.Subscription,
        component: () => <SubscriptionContainer tenantName={props.tenantName} />
      },
      {
        headerText: "Feature Request",
        itemKey: CustomerViewTabs.FeatureRequest,
        component: () => (
          <div style={{ marginLeft: 8, marginRight: 8 }}>
            <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #EDEBE9", padding: "0 4px" }}>
              <TabsContainer
                ariaLabel="feature-request-view-mode-pivot"
                handleSelectedTab={handleFeatureRequestViewModeChange}
                selectedKey={featureRequestViewModeKey}
                styles={{ root: { borderBottom: "none" } }}
              >
                <PivotItem headerText="View by Tenant" itemKey={CustomerViewTabs.FeatureRequestViewByTenant} />
                <PivotItem headerText="View by TPID" itemKey={CustomerViewTabs.FeatureRequestViewByTPID} />
              </TabsContainer>
            </div>
            <div style={{ marginTop: 0 }}>
              <FeatureRequestContainer
                tenantName={props.tenantName}
                viewMode={featureRequestViewModeKey === CustomerViewTabs.FeatureRequestViewByTPID ? "tpid" : "tenant"}
                tpid={featureRequestTpid}
              />
            </div>
          </div>
        )
      },
      {
        headerText: "Support",
        itemKey: CustomerViewTabs.Support,
        component: () => <SupportContainer tenantName={props.tenantName} />
      },
      {
        headerText: "Customer Engagements",
        itemKey: CustomerViewTabs.CustomerEngagements,
        component: () => (
          <div style={{ marginLeft: 8, marginRight: 8 }}>
            <TabsContainer
              aria-label={CUSTOMER_ENGAGEMENTS_TABS_DATA.pivot.ariaLabel}
              handleSelectedTab={handleEngagementTabSelection}
              selectedKey={selectedEngagementKey}
            >
              {renderCustomerEngagementsTabs}
            </TabsContainer>
          </div>
        )
      },
      {
        headerText: "Partner",
        itemKey: CustomerViewTabs.Partner,
        component: () => <PartnerContainer tenantName={props.tenantName} />
      },
      {
        headerText: "Community",
        itemKey: CustomerViewTabs.CommunityDetails,
        component: () => <CommunityDetailsContainer tenantName={props.tenantName} />
      },
      {
        headerText: "Ready for AI",
        itemKey: CustomerViewTabs.BaselineSecurityHealth,
        component: () => {
          const tenantIdFromUrl = (searchParams.get("id") ?? "").toLowerCase();
          return (
            <div style={{ margin: "12px 8px" }}>
              <BaselineSecurityHealthCustomerTab tenantId={tenantIdFromUrl} tenantName={props.tenantName} />
            </div>
          );
        }
      },
      {
        headerText: "Customer Business Health",
        itemKey: CustomerViewTabs.CustomerHealth,
        component: () => (
          <div style={{ marginLeft: 8, marginRight: 8 }}>
            <div style={{ display: "flex", alignItems: "center", borderBottom: "1px solid #EDEBE9", padding: "0 4px" }}>
              <TabsContainer
                aria-label="customer-health-view-mode-pivot"
                handleSelectedTab={handleHealthViewModeChange}
                selectedKey={selectedHealthViewModeKey}
                styles={{ root: { borderBottom: "none" } }}
              >
                <PivotItem headerText="View by Tenant" itemKey={CustomerViewTabs.CustomerHealthViewByTenant} />
                <PivotItem headerText="View by TPID" itemKey={CustomerViewTabs.CustomerHealthViewByTPID} />
              </TabsContainer>

              <div style={{ width: 1, height: 24, backgroundColor: "#C8C6C4", margin: "0 12px", flexShrink: 0 }} />

              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                {filteredHealthProductTabs.map(({ headerText, itemKey }) => {
                  const isActive = activeHealthTab?.itemKey === itemKey;
                  return (
                    <button
                      key={itemKey}
                      onClick={() => handleHealthProductTabClick(headerText)}
                      style={{
                        padding: "5px 16px",
                        borderRadius: 16,
                        border: isActive ? "none" : "1px solid #8A8886",
                        backgroundColor: isActive ? "#0078D4" : "transparent",
                        color: isActive ? "#FFFFFF" : "#323130",
                        fontSize: 13,
                        fontWeight: isActive ? 600 : 400,
                        cursor: "pointer",
                        lineHeight: "20px",
                        transition: "all 0.15s ease"
                      }}
                    >
                      {headerText}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginTop: 0 }}>
              {activeHealthComponent}
            </div>
          </div>
        )
      }
    ]
  };

  const CUSTOMER_ENGAGEMENTS_TABS_DATA = {
    pivot: { ariaLabel: "customer-egagements-pivot-component-aria-label" },
    pivotItems: [
      {
        headerText: "Customer Product",
        itemKey: CustomerViewTabs.CRMCustomerProduct,
        component: () => (
          <div style={{ marginBottom: 13 }}>
            <CRMCustomerProductContainer tenantName={props.tenantName} />
          </div>
        )
      },
      {
        headerText: "Engagements",
        itemKey: CustomerViewTabs.Engagements,
        component: () => (
          <div style={{ marginBottom: 13 }}>
            <CustomerEngagementContainer tenantName={props.tenantName} />
          </div>
        )
      },
      {
        headerText: "Programs",
        itemKey: CustomerViewTabs.Programs,
        component: () => (
          <div style={{ marginBottom: 13 }}>
            <ProgramsContainer tenantName={props.tenantName} />
          </div>
        )
      },
      {
        headerText: "RFA",
        itemKey: CustomerViewTabs.RFACustomerProduct,
        component: () => (
          <div style={{ marginBottom: 13 }}>
            <RFACustomerProductContainer tenantName={props.tenantName} />
          </div>
        )
      }
    ]
  };

  // Add state and logic for Customer Engagements section
  const selectedEngagementSection = searchParams.get("section") || defaultEngagementSection;
  const selectedEngagementKey =
    CUSTOMER_ENGAGEMENTS_TABS_DATA.pivotItems.filter(({ headerText }) => headerText === selectedEngagementSection)[0]
      ?.itemKey || defaultEngagementSection;

  // Handle Customer Engagements tab selection
  const handleEngagementTabSelection = (item?: PivotItem) => {
    if (item) {
      const newSection = item.props.headerText;
      const currentId = searchParams.get("id");
      const currentTab = searchParams.get("tab");
      setSearchParams({
        id: currentId || "",
        tab: currentTab || defaultTab,
        section: newSection || defaultEngagementSection
      });
    }
  };
  const uiEnablementData = useGetUIEnablementsSettingsQuery(undefined)?.data || [];
  const disabled = buildDisabledFeatureLookup(uiEnablementData);
  const disabledTabs = disabled["tab"] || [];
  const disabledSubTabs = disabled["subTab"] || [];

  const renderCustomerEngagementsTabs = CUSTOMER_ENGAGEMENTS_TABS_DATA.pivotItems
    .filter(
      ({ headerText }) =>
        !disabledSubTabs
          .filter((item) => item.feature === "Explore Customers" && item.tab === "Customer Engagements")
          .map((item) => item.subTab)
          .includes(headerText)
    )
    .map(({ headerText, itemKey, component }, index) => (
      <PivotItem headerText={headerText} itemKey={itemKey} key={`${itemKey + index}`}>
        {component()}
      </PivotItem>
    ));


  const CUSTOMER_HEALTH_TABS_DATA = {
    pivot: { ariaLabel: "customer-health-pivot-component-aria-label" },
    pivotItems: [
      {
        headerText: "MTP",
        itemKey: CustomerViewTabs.CustomerHealthMTP,
        component: (viewMode: HealthViewMode, identifierId?: string) => (
          <div style={{ marginBottom: 13 }}>
            <CustomerHealthMTP tenantName={props.tenantName} viewMode={viewMode} identifierId={identifierId} />
          </div>
        )
      },
      {
        headerText: "Purview",
        itemKey: CustomerViewTabs.CustomerHealthPurview,
        component: (viewMode: HealthViewMode, identifierId?: string) => (
          <div style={{ marginBottom: 13 }}>
            <CustomerHealthPurview tenantName={props.tenantName} viewMode={viewMode} identifierId={identifierId} />
          </div>
        )
      },
      {
        headerText: "Intune",
        itemKey: CustomerViewTabs.CustomerHealthIntune,
        component: (viewMode: HealthViewMode, identifierId?: string) => (
          <div style={{ marginBottom: 13 }}>
            <CustomerHealthIntune tenantName={props.tenantName} viewMode={viewMode} identifierId={identifierId} />
          </div>
        )
      },
    ]
  };

  // Health view mode state: "View by Tenant" or "View by TPID"
  const selectedHealthViewMode = searchParams.get("viewMode") || defaultHealthViewMode;
  const healthViewMode: HealthViewMode = selectedHealthViewMode === "View by TPID" ? "tpid" : "tenant";
  const selectedHealthViewModeKey = healthViewMode === "tpid"
    ? CustomerViewTabs.CustomerHealthViewByTPID
    : CustomerViewTabs.CustomerHealthViewByTenant;
  const topParentId = props?.topParentId?.toString() || "";

  // Add state and logic for Customer Health
  const selectedHealthSection = searchParams.get("section") || defaultHealthSection;
  const selectedHealthKey =
    CUSTOMER_HEALTH_TABS_DATA.pivotItems.filter(({ headerText }) => headerText === selectedHealthSection)[0]
      ?.itemKey || defaultHealthSection;

  // Handle Health view mode toggle (View by Tenant / View by TPID)
  const handleHealthViewModeChange = (item?: PivotItem) => {
    if (item) {
      const newViewMode = item.props.headerText;
      const currentId = searchParams.get("id");
      const currentTab = searchParams.get("tab");
      const currentSection = searchParams.get("section") || defaultHealthSection;
      setSearchParams({
        id: currentId || "",
        tab: currentTab || defaultTab,
        section: currentSection,
        viewMode: newViewMode || defaultHealthViewMode
      });
    }
  };

  const filteredHealthProductTabs = CUSTOMER_HEALTH_TABS_DATA.pivotItems
    .filter(
      ({ headerText }) =>
        !disabledSubTabs
          .filter((item) => item.feature === "Explore Customers" && item.tab === "Customer Health")
          .map((item) => item.subTab)
          .includes(headerText)
    )
    .filter(({ headerText }) => !(healthViewMode === "tpid" && headerText === "Intune"));

  const handleHealthProductTabClick = (headerText: string) => {
    const currentId = searchParams.get("id");
    const currentTab = searchParams.get("tab");
    const currentViewMode = searchParams.get("viewMode") || defaultHealthViewMode;
    setSearchParams({
      id: currentId || "",
      tab: currentTab || defaultTab,
      section: headerText,
      viewMode: currentViewMode
    });
  };

  const activeHealthTab = filteredHealthProductTabs.find(({ itemKey }) => itemKey === selectedHealthKey)
    || filteredHealthProductTabs[0];
  const activeHealthComponent = activeHealthTab
    ? activeHealthTab.component(healthViewMode, healthViewMode === "tpid" ? topParentId : undefined)
    : null;

  const renderProductViewTabs = TABS_DATA.pivotItems
    .filter(
      ({ headerText }) =>
        !disabledTabs
          .filter((item) => item.feature === "Explore Customers")
          .map((item) => item.tab)
          .includes(headerText)
    )
    .map(({ headerText, itemKey, component }, index) => (
      <PivotItem headerText={headerText} itemKey={itemKey} key={`${itemKey + index}`}>
        {component()}
      </PivotItem>
    ));

  const handleSelectedTab = (item?: PivotItem) => {
    if (item) {
      const newTab = item.props.itemKey as CustomerViewTabs;
      const currentId = searchParams.get("id");

      // If switching to Customer Engagements tab, include default section
      if (newTab === CustomerViewTabs.CustomerEngagements) {
        setSearchParams({
          id: currentId || "",
          tab: newTab,
          section: defaultEngagementSection
        });
      } else if (newTab === CustomerViewTabs.CustomerHealth) {
        setSearchParams({
          id: currentId || "",
          tab: newTab,
          section: defaultHealthSection,
          viewMode: defaultHealthViewMode
        });
      } else {
        setSearchParams({
          id: currentId || "",
          tab: newTab
        });
      }
      setSelectedTab(newTab || defaultTab);
    }
  };

  React.useEffect(() => {
    const tab = searchParams.get("tab");
    const section = searchParams.get("section");
    const currentId = searchParams.get("id");

    if (tab && Object.values(CustomerViewTabs).includes(tab as CustomerViewTabs)) {
      if (tab === CustomerViewTabs.FeatureRequestViewByTenant) {
        setSelectedTab(CustomerViewTabs.FeatureRequest);
        setFeatureRequestViewModeKey(CustomerViewTabs.FeatureRequestViewByTenant);
      } else if (tab === CustomerViewTabs.FeatureRequestViewByTPID) {
        setSelectedTab(CustomerViewTabs.FeatureRequest);
        setFeatureRequestViewModeKey(CustomerViewTabs.FeatureRequestViewByTPID);
      } else {
        setSelectedTab(tab);
      }

      // If Customer Engagements tab is selected but no section parameter, add default section to URL
      if (tab === CustomerViewTabs.CustomerEngagements && !section && currentId) {
        setSearchParams({ id: currentId, tab: tab, section: defaultEngagementSection });
      }
      // If Customer Health tab is selected but no section parameter, add default section to URL
      if (tab === CustomerViewTabs.CustomerHealth && !section && currentId) {
        setSearchParams({ id: currentId, tab: tab, section: defaultHealthSection, viewMode: defaultHealthViewMode });
      }
    } else {
      // Default to Product Telemetry (productv2) and set it in the URL
      setSelectedTab(defaultTab);
      if (currentId) {
        setSearchParams({ id: currentId, tab: defaultTab });
      }
    }

  }, []);

  React.useEffect(() => {
    return () => {
      if (window.location.pathname !== "/customer") {
        sessionStorage.removeItem("featureTelemetry_lastViewedFamily");
        sessionStorage.removeItem("productTelemetry_lastViewedFamily");
      }
    };
  }, []);

  return (
    <Pivot aria-label={TABS_DATA.pivot.ariaLabel} onLinkClick={handleSelectedTab} selectedKey={selectedTab}>
      {renderProductViewTabs}
    </Pivot>
  );
};
